import React, { isValidElement, useMemo } from "react";
import { Grid } from "../../Grid/Grid";
import { Icon } from "../../Icon/Icon";
import { Heading, Body } from "../../Typography/Typography";
import { CardHeadProps } from "../types";
import { Details } from "../../Details/Details";
import cx from "components/src/utils/cx";
import { tailwindSemanticBgColors } from "shared/src/styles/colorStyles";

export const CardHead = ({
  title,
  description,
  actionText,
  leadingIcon,
  trailingIcon,
  details = [],
  onClick,
  collapsible,
  bg = "bg-default",
  fg = "fg-default",
  headingSize = "md-bold",
  icon,
  isOpen,
  animation,
  paddingClasses = "cui-py-sm cui-px-md",
  showActiveAndHoverState = true,
}: CardHeadProps) => {
  if (!title && !description && !actionText && !details.length) return null;

  const hasIcon = icon || leadingIcon;
  const _leadingIcon = useMemo(()=>{
    if (leadingIcon && isValidElement(leadingIcon)) return leadingIcon;
    if (typeof leadingIcon === "object" && "name" in leadingIcon) return <Icon size="xl" {...leadingIcon} className="cui-mr-2" />
    if (typeof icon === "string" && !!icon) return <Icon name={icon} className="cui-mr-2" />
  },[icon, leadingIcon]);

  return (
    <Grid
      gap="none"
      className={cx(
        {
          [tailwindSemanticBgColors[bg]]: true,
          [paddingClasses]: true,
          "hover:cui-bg-bg-layer-one active:cui-bg-bg-layer-two": showActiveAndHoverState,
          "cui-cursor-pointer": typeof onClick === "function"
        },
        "cui__card-header cui-select-none"
      )}
      flush
      onClick={onClick}
    >
      <Grid.Cell colSpan={9} className="cui-flex cui-items-center">
      {hasIcon ? _leadingIcon : null}
        {title ? (
          <div
            className="cui-flex-col cui-items-center"
            role="presentation"
          >
            <Heading
              size={headingSize}
              role="heading"
              aria-level={1}
              className="cui-select-none"
              color={fg}
            >
              {title}
            </Heading>
            {description ? (
          <Body size="sm" color={fg ? fg : "fg-moderate"} tag="div">
            {description}
          </Body>
        ) : null}
          </div>
        ) : null}
      </Grid.Cell>
      <Grid.Cell colSpan={3} items="center">
        <span className="cui-w-full cui-inline-flex cui-justify-end">
          {details.length ? <Details list={details} /> : null}
          {actionText ? (
            <Body tag="span" size="sm-bold" color={fg}>
              {actionText}
            </Body>
          ) : null}
          {collapsible ? (
            <Icon
              name="ui_chevron-up"
              size="md"
              color={fg ? fg : "fg-moderate"}
              className={cx(
                {
                  "cui-transition-transform cui-duration-200": !!animation,
                  "cui-rotate-180": !isOpen,
                },
                "cui-ml-2"
              )}
            />
          ) : null}
        </span>
      </Grid.Cell>
    </Grid>
  );
};

CardHead.displayName = "CardHeader";
